import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardContent, Heading, Text, Flex, TagBadge, Banner, LoadingOrbit } from '@chatsdet/ui';
import { GithubLogo, Gauge, WarningCircle } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import type { LinkedRepo } from './PropertyCodeTab.types';

interface AwsMonitorConfig {
  awsConnection: string;
  /** Bare name, or `{ name, awsConnection }` when the pipeline is watched through its own connection. */
  pipeline: string | { name: string; awsConnection?: string };
  service?: { cluster: string; name: string };
  alarms: string[];
  logGroups: string[];
}

interface StageMonitor {
  id: string;
  propertyGithubRepoId: string;
  stage: string;
  provider: string;
  filePath: string;
  sourceSha: string;
  config: Record<string, unknown> | null;
  parseError: string | null;
}

interface PropertyCodeMonitorsSectionProps {
  propertyId: string;
  linkedRepos: LinkedRepo[];
}

export function PropertyCodeMonitorsSection({
  propertyId,
  linkedRepos,
}: PropertyCodeMonitorsSectionProps) {
  const monitorsQuery = useQuery({
    queryKey: ['property-monitors', propertyId],
    queryFn: async (): Promise<{ ok: boolean; items: StageMonitor[] }> => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/monitors`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch stage monitors');
      return res.json();
    },
  });

  const repoById = new Map(linkedRepos.map((r) => [r.id, r]));
  const monitors = monitorsQuery.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <div>
          <Heading level={3}>Stage Monitors</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            Deployment stage observability discovered from{' '}
            <code>cofactor/monitors/&lt;stage&gt;.yml</code> in linked repositories
          </Text>
        </div>
      </CardHeader>
      <CardContent>
        {monitorsQuery.isLoading ? (
          <Flex align="center" justify="center" className="py-8">
            <LoadingOrbit size="md" />
          </Flex>
        ) : monitorsQuery.isError ? (
          <Banner variant="error">
            <Flex align="center" gap="sm">
              <WarningCircle size={16} className="shrink-0" />
              <Text size="sm">Couldn&apos;t load stage monitors. Reload the page to try again.</Text>
            </Flex>
          </Banner>
        ) : monitors.length > 0 ? (
          <div className="space-y-2">
            {monitors.map((monitor) => {
              const repo = repoById.get(monitor.propertyGithubRepoId);
              // Only the `aws` provider's config has this shape — a future
              // provider's config is a different shape entirely, so render
              // just the provider name for anything else rather than casting
              // blind.
              const config =
                monitor.parseError || !monitor.config || monitor.provider !== 'aws'
                  ? null
                  : (monitor.config as unknown as AwsMonitorConfig);
              return (
                <div key={monitor.id} className="py-3 border-b border-border last:border-0">
                  {monitor.parseError ? (
                    <Banner variant="error">
                      <Flex direction="column" gap="xs" className="w-full">
                        <Flex align="center" gap="sm">
                          <WarningCircle size={16} className="shrink-0" />
                          <span className="font-medium text-sm">{monitor.stage}</span>
                          <TagBadge name="Broken" color="red" hideDot className="text-xs" />
                          {repo && (
                            <Flex align="center" gap="xs" className="text-xs opacity-80">
                              <GithubLogo size={12} />
                              {repo.repoFullName}
                            </Flex>
                          )}
                        </Flex>
                        <Text size="xs" className="font-mono opacity-90">
                          {monitor.filePath}
                        </Text>
                        <Text size="sm">{monitor.parseError}</Text>
                      </Flex>
                    </Banner>
                  ) : (
                    <Flex align="start" gap="sm">
                      <Gauge size={20} className="text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <Flex align="center" gap="sm">
                          <span className="font-medium">{monitor.stage}</span>
                          <TagBadge name={monitor.provider} color="gray" hideDot className="text-xs" />
                        </Flex>
                        <Flex align="center" gap="sm" className="mt-0.5 flex-wrap">
                          {config?.pipeline && (
                            <Text size="xs" className="text-muted-foreground">
                              Pipeline:{' '}
                              {typeof config.pipeline === 'string'
                                ? config.pipeline
                                : `${config.pipeline.name}${config.pipeline.awsConnection ? ` (via ${config.pipeline.awsConnection})` : ''}`}
                            </Text>
                          )}
                          {config?.service && (
                            <Text size="xs" className="text-muted-foreground">
                              Service: {config.service.cluster}/{config.service.name}
                            </Text>
                          )}
                          {config && (
                            <Text size="xs" className="text-muted-foreground">
                              {config.alarms.length} alarm{config.alarms.length === 1 ? '' : 's'}
                            </Text>
                          )}
                          {config && (
                            <Text size="xs" className="text-muted-foreground">
                              {config.logGroups.length} log group{config.logGroups.length === 1 ? '' : 's'}
                            </Text>
                          )}
                        </Flex>
                        <Flex align="center" gap="sm" className="mt-0.5">
                          <Text size="xs" className="text-muted-foreground font-mono">
                            {monitor.filePath}
                          </Text>
                          {repo && (
                            <Flex align="center" gap="xs" className="text-xs text-muted-foreground">
                              <GithubLogo size={12} />
                              {repo.repoFullName}
                            </Flex>
                          )}
                        </Flex>
                      </div>
                    </Flex>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <Flex direction="column" align="center" gap="md" className="py-8">
            <div className="p-3 bg-muted rounded-full">
              <Gauge size={24} className="text-muted-foreground" />
            </div>
            <Text size="sm" className="text-muted-foreground text-center max-w-sm">
              No stage monitors found. Add a <code>cofactor/monitors/&lt;stage&gt;.yml</code> file to a
              linked repository&apos;s default branch, then rescan.
            </Text>
          </Flex>
        )}
      </CardContent>
    </Card>
  );
}
